import { useEffect, useRef, useState } from 'react';

/**
 * Shared driver for the reassurance cue shown when the user has text ready
 * to save. Used by PrayPage, PrayerDetail update form, UpdateSheet (both
 * variants), and PrayerDetail's TestimonyModal so the "tap save" grammar
 * is identical everywhere.
 *
 * Fires on two triggers:
 *
 * 1. VOICE: the edge where `isCapturing` flips true → false AND the user
 *    actually dictated something. Every mic-stop gets its own reminder —
 *    each recording is a distinct user action.
 *
 * 2. TYPING: a pause in the user's typing — ~2s of no content change
 *    with text present. Fires ONCE per composition (shownForThisTyping
 *    gate) so it doesn't nag a contemplative writer who naturally
 *    pauses between sentences. Resets automatically when content is
 *    cleared (new composition begins).
 *
 * Voice always takes priority — if the user is actively recording, any
 * queued typing-pause timer is canceled. The 5s show duration is identical
 * across both trigger paths so the visual feel is the same.
 */
export function useSaveCue(isCapturing: boolean, content: string): boolean {
  const hasContent = !!content.trim();

  const [show, setShow] = useState(false);
  const shownForThisTypingRef = useRef(false);
  const wasCapturingRef = useRef(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fireCue = () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    setShow(true);
    showTimerRef.current = setTimeout(() => {
      setShow(false);
      showTimerRef.current = null;
    }, 5000);
  };

  // Voice trigger: recording ended with content present.
  useEffect(() => {
    if (isCapturing) {
      if (show) setShow(false);
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
    } else if (wasCapturingRef.current && hasContent) {
      fireCue();
      // Mark typing-path as already served — no need to nag again on
      // a subsequent pause if the user was going to tap save anyway.
      shownForThisTypingRef.current = true;
    }
    wasCapturingRef.current = isCapturing;
    // show / fireCue intentionally omitted: we read show for cleanup
    // and fireCue is locally stable; including either would retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCapturing, hasContent]);

  // Typing trigger: after a pause with content, fire once per composition.
  useEffect(() => {
    // Content cleared → reset the typing gate for the next composition.
    if (!hasContent) {
      shownForThisTypingRef.current = false;
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
      return;
    }
    // Voice active or already served → skip.
    if (isCapturing || shownForThisTypingRef.current) return;

    // Debounce: restart on every keystroke so the cue only fires when the
    // user genuinely pauses (not mid-word).
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      shownForThisTypingRef.current = true;
      fireCue();
      pauseTimerRef.current = null;
    }, 2000);

    return () => {
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, hasContent, isCapturing]);

  useEffect(() => () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
  }, []);

  return show;
}
